// Consumer-side config for the storefront header (NavBar) and footer chrome, shared
// across pages. Persisted as JSON strings on IStorefrontSettings.headerConfig /
// .footerConfig. Mirrors the admin editor (ac-main/web: components/Storefront/Editor/
// headerFooterConfig.ts). Each section reuses the generic search-config style shape
// (ISearchElementStyle) and is applied with elementStyleFrom — numbers are px, fontSize
// is a percent, and any camelCase CSS passes straight through.

import { ISearchElementStyle } from './searchPageConfig';
import { ILayout } from './layout';

export interface IHeaderConfig {
    // Opt-in grid layout for the whole header (AC-937). When present and valid, the layout
    // renderer takes over; absent/empty/malformed ⇒ today's fixed header. Additive.
    layout?: ILayout;
    // When true, the header bar stays pinned to the top of the viewport while the page
    // scrolls (position: fixed) instead of scrolling away with the content.
    sticky?: boolean;
    // Header search placement (AC-970). When `searchRow` is true, the header search renders as a
    // full-width row BELOW the header bar instead of inline in the actions area. `searchRowSticky`
    // pins that search row to the top on scroll, independently of the header's own `sticky`.
    searchRow?: boolean;
    searchRowSticky?: boolean;
    // Style overrides for the search-row band itself (background, padding, color, …). Applied over the
    // row's defaults via elementStyleFrom, so e.g. a `background` shorthand can give the row a full
    // image/parallax band (background-attachment: fixed) instead of the flat menu-bar color.
    searchRowStyle?: ISearchElementStyle;
    // When true, suppresses the auto-generated "Menu" dropdown fed by storefrontMenuLinks
    // (Cosmos type=header link docs). Opt-in only — absent/false renders exactly as today.
    // Storefronts that already surface those links via headerLinksConfig's inline nav (the
    // .header-center row) use this to avoid showing the same links twice. Does not affect the
    // inline header links or narrow-screen nav: on narrow layouts, custom header links still
    // get their own standalone "More" button when this auto menu is hidden.
    hideAutoMenu?: boolean;
    // Opt-in: when true, the logo+title move from the far-left brand slot into the FIRST
    // position of the centered header-links group (.header-center), so logo + inline links sit
    // centered together as one group (e.g. "[logo] Home  About us  Pricing  ..."), instead of
    // the logo pinned to the far edge with links centered in the remaining space. Only takes
    // effect on desktop widths when there's an actual centered links group to join (headerLinks
    // non-empty); otherwise the brand falls back to its normal left-pinned slot so it's never
    // dropped. Absent/false renders exactly as today (brand stays left-pinned).
    brandInline?: boolean;
    // The header bar itself (<nav>).
    bar?: ISearchElementStyle;
    // The store-name / title text.
    title?: ISearchElementStyle;
    // Overrides the text shown next to the header logo (defaults to the storefront's `name`).
    // Set to '' to show the logo alone — e.g. when the logo is a wordmark that already spells
    // the store name and repeating it beside the image would read twice.
    titleText?: string;
    // The header logo <img> (its default height is 3em; override height, borderRadius, …).
    logo?: ISearchElementStyle;
    // The logo+title container (.header-title) that holds the logo image and the title text
    // together — style this to control their alignment/gap/padding as a group.
    brand?: ISearchElementStyle;
    // The header search input (shown when useHeaderSearch is on).
    searchBar?: ISearchElementStyle;
    // The "Menu" dropdown trigger button.
    menuButton?: ISearchElementStyle;
    // The "Menu" dropdown popover surface.
    menu?: ISearchElementStyle;
    // The section headers inside the "Menu" dropdown (separator entries).
    menuHeader?: ISearchElementStyle;
    // The individual entries (links) inside the "Menu" dropdown.
    menuItem?: ISearchElementStyle;
    // The mega-menu panel surface (full-width bar + popover mega). Absent = a luminance-aware default
    // derived from the header (dark panel + light text on a dark bar, white on a light bar). Set a
    // `background`/`backgroundColor` (plus optional `color`, `borderTop`, `boxShadow`, `padding`) to
    // pin the panel's look explicitly. AC-972.
    megaPanel?: ISearchElementStyle;
    // ── The account / login persona in the header ────────────────────────────────────────
    // One set of parts covers BOTH states, because to a shopper it is one control that changes label:
    // signed out it reads "Guest / Sign in" and opens the login form; signed in it shows the shopper's
    // name and opens the account menu. Absent leaves each state's built-in look untouched.
    /** The clickable pill/trigger itself. Signed out this defaults to the light-grey rounded pill. */
    account?: ISearchElementStyle;
    /** Primary line: the shopper's name, or "Guest". */
    accountName?: ISearchElementStyle;
    /** Secondary line: "Sign in". Signed out only. */
    accountSubtext?: ISearchElementStyle;
    /** The round avatar. */
    accountAvatar?: ISearchElementStyle;
    /** The sign-in popover surface. */
    accountPanel?: ISearchElementStyle;
}

export interface IFooterConfig {
    // Opt-in grid layout for the whole footer (AC-937). Same gate as the header. Additive.
    layout?: ILayout;
    // The footer bar itself (.footer-content).
    bar?: ISearchElementStyle;
    // The inner container that wraps the three footer columns (logo/text, links, contact) —
    // i.e. the block ABOVE the social row. Style this to constrain the columns' width and draw a
    // divider between them and the socials, like many marketing-site footers do. Common uses:
    // { maxWidth: 1200 } to cap + auto-center the columns, and
    // { borderBottom: '1px solid rgba(0,0,0,0.12)', paddingBottom: 32 } for the hairline divider.
    // Absent renders the columns full-width with no divider (today's behavior).
    contentContainer?: ISearchElementStyle;
    // The left column (logo + footer text).
    leftText?: ISearchElementStyle;
    // The right column (contact details / email / phone).
    rightText?: ISearchElementStyle;
    // The footer link section headers (separator entries).
    linkHeader?: ISearchElementStyle;
    // The regular footer links.
    link?: ISearchElementStyle;
    // The circular badge wrapped around each footer social icon (LinkedIn/Facebook/…).
    // Set e.g. { backgroundColor: '#ff5800' } to render brand-colored round badges; when a
    // backgroundColor is present each icon is drawn as a clean white vector glyph (color overridable
    // via `color`) sitting on the badge, instead of the native full-color PNG. Absent renders the
    // icons as plain/native images (today's behavior). Numbers are px; borderRadius defaults to a
    // full circle and diameter defaults to 40px (override width/height).
    socialBadge?: ISearchElementStyle;
    // Hover state for the circular social badges — e.g. { backgroundColor: '#2f3e4a' } to shift the
    // badge to a slate on hover (with a smooth transition), like many marketing-site footers do.
    // Only applies when socialBadge draws a badge. Absent = no hover change.
    socialBadgeHover?: ISearchElementStyle;
    // Horizontal alignment of the social-icon row: 'left' | 'center' | 'right' (default 'right',
    // today's bottom-right placement). 'center' centers the row across the footer.
    socialAlign?: 'left' | 'center' | 'right';
}

export function parseHeaderConfig(raw?: string): IHeaderConfig {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? (parsed as IHeaderConfig) : {};
    } catch {
        return {};
    }
}

export function parseFooterConfig(raw?: string): IFooterConfig {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? (parsed as IFooterConfig) : {};
    } catch {
        return {};
    }
}
